import { motion } from "framer-motion";
import {
  FileText,
  BookOpen,
  Trophy,
  Receipt,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring" as const, stiffness: 100 },
  },
};

interface AppCardProps {
  title: string;
  description: string;
  icon: React.ElementType;
  href: string;
  gradient: string;
  audience?: string;
  comingSoon?: boolean;
  external?: boolean;
}

function AppCard({
  title,
  description,
  icon: Icon,
  href,
  gradient,
  audience,
  comingSoon,
  external,
}: AppCardProps) {
  const content = (
    <Card
      className={`group relative h-full overflow-hidden border-2 transition-all duration-300 ${
        comingSoon
          ? "border-border/30 opacity-70 cursor-not-allowed"
          : "border-border/50 hover:border-primary/50 hover:shadow-xl hover:-translate-y-1 cursor-pointer"
      }`}
    >
      {/* Gradient background overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 transition-opacity duration-300 ${
          !comingSoon && "group-hover:opacity-5"
        }`}
      />

      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div
            className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${gradient} shadow-lg transition-transform duration-300 ${
              !comingSoon && "group-hover:scale-110"
            }`}
          >
            <Icon className="h-7 w-7 text-white" />
          </div>
          <div className="flex flex-col items-end gap-1">
            {audience && (
              <Badge variant="secondary" className="text-xs font-normal">
                {audience}
              </Badge>
            )}
            {comingSoon && (
              <Badge
                variant="outline"
                className="text-xs border-amber-500/50 text-amber-600"
              >
                Coming Soon
              </Badge>
            )}
          </div>
        </div>
        <CardTitle className="mt-4 text-xl font-bold">{title}</CardTitle>
        <CardDescription className="text-sm leading-relaxed">
          {description}
        </CardDescription>
      </CardHeader>

      <CardContent className="pt-0">
        <div
          className={`flex items-center gap-2 text-sm font-medium transition-colors ${
            comingSoon
              ? "text-muted-foreground/50"
              : "text-primary group-hover:text-primary"
          }`}
        >
          {comingSoon ? "In Development" : "Open App"}
          {!comingSoon && (
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (comingSoon) {
    return content;
  }

  if (external) {
    return (
      <a
        href={href}
        className="block h-full"
        target="_blank"
        rel="noopener noreferrer"
      >
        {content}
      </a>
    );
  }

  return (
    <a href={href} className="block h-full">
      {content}
    </a>
  );
}

const apps: AppCardProps[] = [
  {
    title: "Reports",
    description:
      "Create and manage student progress reports with star ratings, AI-enhanced feedback, and beautiful PDF exports.",
    icon: FileText,
    href: "https://reports.tisaverse.nl",
    gradient: "from-blue-500 to-indigo-600",
    audience: "Teachers",
    external: false,
  },
  {
    title: "Library",
    description:
      "Browse books, track loans, and discover new reads. Students can check out books with their PIN.",
    icon: BookOpen,
    href: "/login",
    gradient: "from-emerald-500 to-teal-600",
    audience: "Students",
  },
  {
    title: "Gamification",
    description:
      "Earn TISAcoins, complete quests, join guilds, and collect badges. Turn learning into an adventure!",
    icon: Trophy,
    href: "/login",
    gradient: "from-amber-500 to-orange-600",
    audience: "Students",
  },
  {
    title: "Invoices",
    description:
      "Generate and manage school invoices, track payments, and send automated reminders to parents.",
    icon: Receipt,
    href: "https://invoice.tisaverse.nl",
    gradient: "from-purple-500 to-pink-600",
    audience: "Admin",
    external: true,
  },
];

export default function HomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Header */}
      <header className="border-b bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/80 shadow-md">
              <Sparkles className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold">TISAverse</h1>
              <p className="text-xs text-muted-foreground">
                School Management Suite
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <h2 className="mb-4 text-4xl font-bold tracking-tight sm:text-5xl">
            Welcome to{" "}
            <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              TISAverse
            </span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Your complete school management ecosystem. Choose an app to get
            started.
          </p>
        </motion.div>

        {/* App Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
        >
          {apps.map((app) => (
            <motion.div
              key={app.title}
              variants={itemVariants}
              className="h-full"
            >
              <AppCard {...app} />
            </motion.div>
          ))}
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-16 text-center text-sm text-muted-foreground"
        >
          <p>
            TISA School &copy; {new Date().getFullYear()} &middot; Empowering
            Education
          </p>
        </motion.div>
      </main>
    </div>
  );
}
